import { ArrowRight } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useReducedMotion } from '../../hooks/useReducedMotion';

/**
 * CTA Section - Final call to action.
 * Features gradient background and prominent action buttons.
 */
export function CTASection() {
    const { ref, isInView } = useScrollAnimation<HTMLElement>({ threshold: 0.3 });
    const reducedMotion = useReducedMotion();

    return (
        <section
            ref={ref}
            className="py-24 px-4 md:px-8 lg:px-16 relative overflow-hidden"
            style={{ background: 'var(--gradient-fire)' }}
            aria-labelledby="cta-title"
        >
            {/* Background decoration */}
            <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
                <div
                    className="absolute -top-1/2 -right-1/4 w-[600px] h-[600px] rounded-full opacity-20"
                    style={{ background: 'white' }}
                />
                <div
                    className="absolute -bottom-1/2 -left-1/4 w-[400px] h-[400px] rounded-full opacity-10"
                    style={{ background: 'white' }}
                />
            </div>

            <div className="max-w-4xl mx-auto text-center relative z-10">
                <h2
                    id="cta-title"
                    className={`text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-6 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : reducedMotion ? '' : 'opacity-0 translate-y-8'
                        }`}
                >
                    Ensure Your Design is Safe
                </h2>

                <p
                    className={`text-xl text-white/90 mb-10 max-w-2xl mx-auto transition-all duration-700 delay-100 ${isInView ? 'opacity-100 translate-y-0' : reducedMotion ? '' : 'opacity-0 translate-y-8'
                        }`}
                >
                    Join thousands of engineers who trust Environaise for their fire safety compliance studies.
                </p>

                <div
                    className={`flex flex-col sm:flex-row gap-4 justify-center items-center transition-all duration-700 delay-200 ${isInView ? 'opacity-100 translate-y-0' : reducedMotion ? '' : 'opacity-0 translate-y-8'
                        }`}
                >
                    <a
                        href="/register"
                        className="group px-8 py-4 bg-white text-[var(--fire-red)] rounded-xl font-semibold text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl flex items-center gap-2"
                    >
                        Request Audit
                        <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                    </a>

                    <a
                        href="/login"
                        className="px-8 py-4 border-2 border-white/80 text-white rounded-xl font-semibold text-lg transition-all duration-300 hover:bg-white/10 hover:border-white"
                    >
                        Contact Experts
                    </a>
                </div>

                {/* Trust indicators */}
                <div
                    className={`mt-12 flex flex-wrap justify-center gap-8 items-center text-white/70 text-sm transition-all duration-700 delay-300 ${isInView ? 'opacity-100' : reducedMotion ? '' : 'opacity-0'
                        }`}
                >
                    <span>✓ ISO 9001 Certified</span>
                    <span>✓ Global Standards Compliance</span>
                    <span>✓ 48h Turnaround</span>
                </div>
            </div>
        </section>
    );
}

export default CTASection;
